import { useState, useEffect, useCallback } from 'react';
import { trainingLogApi, Training, TrainingSummary, CreateTrainingData } from '../services/trainingLogApi';

export type TrainingLogTab = 'own' | 'team' | 'all' | 'settings';

export function useTrainingLog() {
    const [trainings, setTrainings] = useState<Training[]>([]);
    const [teamTrainings, setTeamTrainings] = useState<Training[]>([]);
    const [allTrainings, setAllTrainings] = useState<Training[]>([]);
    const [summary, setSummary] = useState<TrainingSummary | null>(null);
    const [teamSummary, setTeamSummary] = useState<TrainingSummary | null>(null);
    const [allSummary, setAllSummary] = useState<TrainingSummary | null>(null);
    const [selectedYear, setSelectedYear] = useState<number | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<TrainingLogTab>('own');

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        // True when the first run only picked the year: the re-triggered run is the
        // complete one, so loading must stay true until then (no half-loaded page).
        let awaitingYear = false;

        try {
            const [ownSummary, ownTrainings] = await Promise.allSettled([
                trainingLogApi.getSummary(selectedYear, 'own'),
                trainingLogApi.list(selectedYear),
            ]);

            if (ownSummary.status === 'fulfilled') {
                setSummary(ownSummary.value);
                // Set initial year from available years if not set
                if (!selectedYear && ownSummary.value.availableYears.length > 0) {
                    const currentYear = new Date().getFullYear();
                    const hasCurrentYear = ownSummary.value.availableYears.includes(currentYear);
                    setSelectedYear(hasCurrentYear ? currentYear : ownSummary.value.availableYears[0]);
                    awaitingYear = true;
                    return; // Will re-trigger with year set; loading stays true
                }
            }
            if (ownTrainings.status === 'fulfilled') {
                setTrainings(ownTrainings.value);
            }

            // Team data for managers, all employees for HR/Admin. The server decides both
            // (EmployeeMaster hierarchy, UserRoles); these flags only spare a 403 round trip.
            const own = ownSummary.status === 'fulfilled' ? ownSummary.value : null;
            const [teamSum, teamData, allSum, allData] = await Promise.allSettled([
                own?.isManager ? trainingLogApi.getSummary(selectedYear, 'team') : Promise.resolve(null),
                own?.isManager ? trainingLogApi.listTeam(selectedYear, 'team') : Promise.resolve([]),
                own?.canSeeAll ? trainingLogApi.getSummary(selectedYear, 'all') : Promise.resolve(null),
                own?.canSeeAll ? trainingLogApi.listTeam(selectedYear, 'all') : Promise.resolve([]),
            ]);
            if (teamSum.status === 'fulfilled') setTeamSummary(teamSum.value);
            if (teamData.status === 'fulfilled') setTeamTrainings(teamData.value);
            if (allSum.status === 'fulfilled') setAllSummary(allSum.value);
            if (allData.status === 'fulfilled') setAllTrainings(allData.value);
            const failed = [teamSum, teamData, allSum, allData].find(r => r.status === 'rejected');
            if (failed && failed.status === 'rejected') {
                setError(failed.reason instanceof Error ? failed.reason.message : 'Failed to load training data');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load training data');
        } finally {
            if (!awaitingYear) setLoading(false);
        }
    }, [selectedYear]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const createTraining = useCallback(async (data: CreateTrainingData) => {
        await trainingLogApi.create(data);
        await loadData();
    }, [loadData]);

    const updateTraining = useCallback(async (id: number, data: CreateTrainingData) => {
        await trainingLogApi.update(id, data);
        await loadData();
    }, [loadData]);

    const deleteTraining = useCallback(async (id: number) => {
        await trainingLogApi.delete(id);
        await loadData();
    }, [loadData]);

    // When switching tabs, the year filter is scope-specific now: a manager
    // may have own trainings in a year where their team has none (Sara had
    // 2025 personal trainings but no team trainings). If the currently-selected
    // year doesn't appear in the target tab's availableYears, snap to the
    // newest year that does.
    const setActiveTabSafely = useCallback((tab: TrainingLogTab) => {
        setActiveTab(tab);
        const years = tab === 'team' ? teamSummary?.availableYears
            : tab === 'all' ? allSummary?.availableYears
            : tab === 'own' ? summary?.availableYears
            : undefined;
        if (years && selectedYear !== undefined && years.length > 0 && !years.includes(selectedYear)) {
            setSelectedYear(years[0]);
        }
    }, [teamSummary, allSummary, summary, selectedYear]);

    return {
        trainings,
        teamTrainings,
        allTrainings,
        summary,
        teamSummary,
        allSummary,
        selectedYear,
        setSelectedYear,
        loading,
        error,
        activeTab,
        setActiveTab: setActiveTabSafely,
        createTraining,
        updateTraining,
        deleteTraining,
        refresh: loadData,
    };
}
